import type { AuthTokens } from '@faktorplus/shared';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';
const TOKEN_STORAGE_KEY = 'faktorplus-tokens';

export interface FieldError {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly fieldErrors: FieldError[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/* ───────────────────────────── token storage ──────────────────────────── */

export const tokenStore = {
  read(): AuthTokens | null {
    try {
      const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as AuthTokens) : null;
    } catch {
      return null;
    }
  },

  write(tokens: AuthTokens): void {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
  },

  clear(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  },
};

/* ──────────────────────────────── requests ────────────────────────────── */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, unknown>;
  /** Set for the refresh call itself, so a failure cannot recurse. */
  skipRefresh?: boolean;
}

let refreshInFlight: Promise<AuthTokens | null> | null = null;

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await sendRequest(path, options);

  if (response.status === 401 && !options.skipRefresh) {
    const tokens = await refreshTokens();
    if (!tokens) {
      // Nothing else observes the cleared tokens, so without this the app keeps
      // rendering an authenticated shell where every panel shows a generic
      // error and the only way out is a manual page refresh.
      redirectToLogin();
      throw new ApiError(401, 'Sesja wygasła. Zaloguj się ponownie.');
    }

    const retried = await sendRequest(path, options);
    return parseResponse<T>(retried);
  }

  return parseResponse<T>(response);
}

async function sendRequest(path: string, options: RequestOptions): Promise<Response> {
  const tokens = tokenStore.read();
  const headers: Record<string, string> = {};

  if (tokens?.accessToken) headers.Authorization = `Bearer ${tokens.accessToken}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  return fetch(`${API_URL}/api${path}${buildQueryString(options.query)}`, {
    method: options.method ?? 'GET',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
}

/**
 * Refreshes at most once at a time: several queries failing together must not
 * each burn a refresh token and log the user out.
 */
async function refreshTokens(): Promise<AuthTokens | null> {
  refreshInFlight ??= (async () => {
    const current = tokenStore.read();
    if (!current?.refreshToken) return null;

    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      });

      if (!response.ok) {
        tokenStore.clear();
        return null;
      }

      const tokens = (await response.json()) as AuthTokens;
      tokenStore.write(tokens);
      return tokens;
    } catch {
      tokenStore.clear();
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

const LOGIN_PATH = '/logowanie';

function redirectToLogin(): void {
  tokenStore.clear();
  if (window.location.pathname !== LOGIN_PATH) window.location.assign(LOGIN_PATH);
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    const body = payload as { message?: string; errors?: FieldError[] } | null;
    throw new ApiError(
      response.status,
      body?.message ?? 'Wystąpił błąd. Spróbuj ponownie.',
      body?.errors ?? [],
    );
  }

  return payload as T;
}

function buildQueryString(query: Record<string, unknown> | undefined): string {
  if (!query) return '';

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, value instanceof Date ? value.toISOString() : String(value));
  }

  const serialised = params.toString();
  return serialised ? `?${serialised}` : '';
}

/**
 * Attachments cannot be loaded with a plain `<img src>` or `<a href>`, because
 * a browser-initiated request carries no Authorization header and the download
 * route requires one. Fetch the bytes, then hand back an object URL.
 *
 * Callers must revoke the URL when the component unmounts.
 */
export async function fetchAttachmentObjectUrl(attachmentId: string): Promise<string> {
  const response = await fetch(`${API_URL}/api/attachments/${attachmentId}`, {
    headers: authorizationHeader(),
  });

  if (response.status === 401) {
    const tokens = await refreshTokens();
    if (!tokens) throw new ApiError(401, 'Sesja wygasła. Zaloguj się ponownie.');
    return fetchAttachmentObjectUrl(attachmentId);
  }

  if (!response.ok) {
    throw new ApiError(response.status, 'Nie udało się pobrać załącznika');
  }

  return URL.createObjectURL(await response.blob());
}

/** Multipart upload; `fetch` sets the boundary itself, so no Content-Type here. */
export async function uploadAttachment(invoiceId: string, file: File): Promise<void> {
  const form = new FormData();
  form.append('file', file);

  const response = await fetch(`${API_URL}/api/invoices/${invoiceId}/attachments`, {
    method: 'POST',
    headers: authorizationHeader(),
    body: form,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(response.status, body?.message ?? 'Nie udało się wgrać pliku');
  }
}

/** Same authorised-bytes dance as attachments, for the site documents route. */
export async function fetchDocumentObjectUrl(documentId: string): Promise<string> {
  const response = await fetch(`${API_URL}/api/documents/${documentId}/download`, {
    headers: authorizationHeader(),
  });

  if (response.status === 401) {
    const tokens = await refreshTokens();
    if (!tokens) throw new ApiError(401, 'Sesja wygasła. Zaloguj się ponownie.');
    return fetchDocumentObjectUrl(documentId);
  }

  if (!response.ok) {
    throw new ApiError(response.status, 'Nie udało się pobrać dokumentu');
  }

  return URL.createObjectURL(await response.blob());
}

/** Multipart upload with form fields riding along the file. */
export async function uploadMultipart(
  path: string,
  fields: Record<string, string>,
  file: File,
): Promise<void> {
  const form = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value) form.append(key, value);
  }
  form.append('file', file);

  const response = await fetch(`${API_URL}/api${path}`, {
    method: 'POST',
    headers: authorizationHeader(),
    body: form,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new ApiError(response.status, body?.message ?? 'Nie udało się wgrać pliku');
  }
}

function authorizationHeader(): Record<string, string> {
  const tokens = tokenStore.read();
  return tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {};
}
